import { useRef, useState, useMemo, useEffect, useCallback } from 'react';
import {
    Button,
    Grid,
    Tooltip
} from '@mui/material';
import DateRangePicker from '@/shared/components/ui/CustomSearch/DateRangePicker';
import { useTableFilter } from "@/shared";
import { Search } from "@mui/icons-material";
import { format } from "date-fns";
import Flatpickr from "react-flatpickr";
import { GeneralTable } from "@/shared/components/ui";
import { exportExcel, mapEgresosToExcel, } from "../../function/exportarExcel";
import { RiFileExcel2Fill } from "react-icons/ri"
import { toast } from 'react-toastify';
import { GetEgresos } from '@/shared/actions/erp/compras/Egreso.actions';
import { useTableColumnsEgreso } from '../EgresoTableDynamic/columnsEgresoReporte';

export default function TabReporteEgresos() {

    const { columnaBase } = useTableColumnsEgreso();

    const flatpickrRef = useRef<Flatpickr>(null);
    // Calcular fechas del mes actual
    const { firstDayCurrentMonth, currentDay } = useMemo(() => {
        const today = new Date();
        const year = today.getFullYear();
        const month = today.getMonth();

        // Primer día del mes actual
        // (JavaScript cuenta los meses desde 0, así que 'month' es el mes actual)
        const firstDay = new Date(year, month, 1);

        return {
            firstDayCurrentMonth: format(firstDay, "yyyy-MM-dd"),
            currentDay: format(today, "yyyy-MM-dd")
        };
    }, []);


    const {
        dateBefore,
        dateAfter,
        onChangeRangeDate,
    } = useTableFilter();

    // Cargar datos al montar (con rango del mes anterior)
    useEffect(() => {
        if (!dateAfter && !dateBefore) {
            // Establecer rango del mes anterior por defecto
            const startDate = new Date(firstDayCurrentMonth);
            const endDate = new Date(currentDay);
            onChangeRangeDate([startDate, endDate]);
        }
    }, []);

    // Estados de datos y carga
    const [allData, setAllData] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    // Paginación para Material React Table (manejada internamente por la tabla)
    const [pagination, setPagination] = useState({
        pageIndex: 0,
        pageSize: 10,
    });

    const filteredData = useMemo(() => {
        let data = allData;
        return data;
    }, [allData]);

    // Paginación en memoria optimizada - solo pasamos las filas visibles a la tabla
    // Esto mejora drásticamente el rendimiento porque GeneralTable solo renderiza 10-50 filas en vez de 4812
    const paginatedData = useMemo(() => {
        const startIdx = pagination.pageIndex * pagination.pageSize;
        const endIdx = startIdx + pagination.pageSize;
        return filteredData.slice(startIdx, endIdx);
    }, [filteredData, pagination.pageIndex, pagination.pageSize]);

    // AbortController para cancelar requests anteriores
    const abortControllerRef = useRef<AbortController | null>(null);

    const loadData = useCallback(async () => {
        // Cancelar request anterior
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        abortControllerRef.current = new AbortController();
        setIsLoading(true);
        try {

            const egreso = await GetEgresos({
                page: 1,
                page_size: 500,
                fecha_after: dateAfter || firstDayCurrentMonth,
                fecha_before: dateBefore || currentDay,
            });
            // Si fue cancelado
            if (abortControllerRef.current.signal.aborted) {
                return;
            }
            if (egreso.data?.data && Array.isArray(egreso.data?.data)) {

                const egresos = egreso.data.data;

                const filas = egresos.flatMap((egreso) =>
                    (egreso?.productos || []).flatMap((producto) => {

                        if (producto.contiene_serie && producto.usa_metros) {
                            return (producto.series || []).map((serie) => ({
                                id_egreso: egreso.id_egreso_inventario,
                                codigo_egreso: egreso.codigo,
                                fecha: egreso.fecha,
                                usuario: egreso.user,
                                motivo: egreso?.movimiento_inventario_data?.[0]?.motivo_data?.nombre || '',
                                observaciones: egreso?.observaciones?.observacion_egreso || '',

                                bodega: egreso?.bodega_data?.nombre || '',
                                ubicacion: egreso?.ubicacion_data?.nombre || '',

                                codigo_producto: producto.codigo,
                                descripcion: producto?.producto_data?.descripcion || '',
                                precio: producto?.producto_data?.ultimo_precio_compra || '',

                                serie,
                                cantidad: producto?.series_metros?.[serie] || 0,
                                usa_metros: true
                            }));
                        }

                        if (producto.contiene_serie && !producto.usa_metros) {
                            return (producto.series || []).map((serie) => ({
                                id_egreso: egreso.id_egreso_inventario,
                                codigo_egreso: egreso.codigo,
                                fecha: egreso.fecha,
                                usuario: egreso.user,
                                motivo: egreso?.movimiento_inventario_data?.[0]?.motivo_data?.nombre || '',
                                observaciones: egreso?.observaciones?.observacion_egreso || '',

                                bodega: egreso?.bodega_data?.nombre || '',
                                ubicacion: egreso?.ubicacion_data?.nombre || '',

                                codigo_producto: producto.codigo,
                                descripcion: producto?.producto_data?.descripcion || '',
                                precio: producto?.producto_data?.ultimo_precio_compra || '',

                                serie,
                                cantidad: 1,
                                usa_metros: false
                            }));
                        }

                        return [{
                            id_egreso: egreso.id_egreso_inventario,
                            codigo_egreso: egreso.codigo,
                            fecha: egreso.fecha,
                            usuario: egreso.user,
                            motivo: egreso?.movimiento_inventario_data?.[0]?.motivo_data?.nombre || '',
                            observaciones: egreso?.observaciones?.observacion_egreso || '',

                            bodega: egreso?.bodega_data?.nombre || '',
                            ubicacion: egreso?.ubicacion_data?.nombre || '',

                            codigo_producto: producto.codigo,
                            descripcion: producto?.producto_data?.descripcion || '',
                            precio: producto?.producto_data?.ultimo_precio_compra || '',

                            serie: null,
                            cantidad: producto.cantidad,
                            usa_metros: false
                        }];
                    })
                );

                setAllData(filas);

                setPagination(prev => ({
                    ...prev,
                    pageIndex: 0,
                }));
            } else {
                toast.error("Estructura de respuesta inesperada");
            }
        } catch (err: any) {
            if (err?.name === "AbortError" || abortControllerRef.current?.signal.aborted) {
                return;
            }
            console.log(err);
            toast.error(err?.message || "Error al cargar datos");
        } finally {
            setIsLoading(false);
        }
    }, [
        dateAfter,
        dateBefore,
        firstDayCurrentMonth,
        currentDay,
    ]);


    const handleSearch = async () => {
        loadData()
    };


    return (
        <>
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <Grid container spacing={1.5}>
                        <Grid item xs={12} sm={6} md={4} lg={3}>
                            <DateRangePicker
                                size={{ xs: 12, sm: 12, md: 12, lg: 12 }}
                                flatpickrRef={flatpickrRef}
                                onChange={(range) => onChangeRangeDate(range)}
                                label="Rango de fechas de Egreso"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3} lg={2}>
                            <Button
                                variant="contained"
                                color="primary"
                                size="medium"
                                startIcon={<Search />}
                                onClick={handleSearch}
                                sx={{
                                    flex: { xs: 1, sm: '0 1 auto' },
                                    minWidth: { xs: 'auto', sm: 140 },
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    boxShadow: 2,
                                    '&:hover': { boxShadow: 4 }
                                }}
                            >
                                Consultar
                            </Button>
                        </Grid>
                    </Grid>
                </Grid>

                {/* Tabla */}
                <Grid item xs={12}>
                    <Grid container spacing={1} justifyContent={'flex-end'}>
                        <Tooltip title="Click">
                            <Button
                                variant="outlined"
                                color="success"
                                onClick={() => exportExcel(mapEgresosToExcel(filteredData), 'reporte_de_egresos')}
                                startIcon={
                                    <RiFileExcel2Fill />
                                }
                            >
                                {'Exportar a Excel'}
                            </Button>
                        </Tooltip>
                    </Grid>

                    <GeneralTable
                        columns={columnaBase}
                        data={paginatedData}
                        isLoading={isLoading}
                        pagination={pagination}
                        onPaging={setPagination}
                        rowCount={filteredData.length}
                        enableColumnActions={false}
                        enableEditing={false}
                        enableGlobalFilter={false}
                        enableColumnFilters={false}
                    />
                </Grid>

            </Grid>
        </>
    );
}
